/**
 * UI Store - Pinia
 *
 * Manages UI state: navigation, modals, notifications, track styling.
 * Migrated from VueX module src/store/modules/ui/
 */

import { defineStore } from 'pinia';

export const useUiStore = defineStore('ui', {
  state: () => ({
    activeMenu: '',
    mainNavStatus: false,
    // Start screen animation state
    startScreenAnimationActive: true,
    // Track Characteristics
    trackStrokeColor: [151, 38, 109, 1], // tailwind's pink-800
    trackStrokeLineCap: 'round',
    trackStrokeWidth: 5,
    // Position Point characteristics
    positionType: 'Circle',
    positionRadius: 8,
    positionFillColor: '#9D174D', // tailwind's pink-800
    positionStrokeColor: 'transparent',
    positionStrokeWidth: 2,
    // Demo Import Modal
    showDemoImportModal: false,
    // Metadata Info Modal (shows what was auto-calculated)
    metadataInfoModal: {
      visible: false,
      filled: [],    // { field: 'distance', value: '5.2 km' }
      warnings: []   // { field: 'duration', reason: 'timestamps missing' }
    },
    // Tag Selector Modal
    tagSelectorModal: {
      visible: false,
      selectedTagIds: [],
      onSave: null  // callback function
    },
    // Colors
    primary: '',
    secondary: '',
    info: '',
    success: '',
    warning: '',
    error: ''
  }),

  getters: {
    // Simple state accessors can be accessed directly via store.activeMenu
    // But keeping these for backwards compatibility during migration
    getActiveMenu: (state) => state.activeMenu,
    getMainNavStatus: (state) => state.mainNavStatus,
    getStartScreenAnimationActive: (state) => state.startScreenAnimationActive,
    getTrackStrokeColor: (state) => state.trackStrokeColor,
    getTrackStrokeWidth: (state) => state.trackStrokeWidth
  },

  actions: {
    /**
     * Remove navigation-related classes from body
     */
    removeAllNavigationClassesFromBody () {
      const list = [...document.body.classList];
      list.forEach(el => {
        if (el.includes('active') || el.includes('notes') || el.includes('activity') || el.includes('tracked')) {
          document.body.classList.remove(el);
        }
      });
      return null;
    },

    /**
     * Set the active menu and manage body classes
     */
    setActiveMenu (what) {
      // Close all (happens when main menu closes)
      if (what === '') {
        this.removeAllNavigationClassesFromBody();
      }

      // first opening
      if (what !== '' && this.activeMenu === '') {
        document.body.classList.add('active-' + what);
      }

      // Close old menu, open new one
      if (what !== this.activeMenu && what !== '' && this.activeMenu !== '') {
        this.removeAllNavigationClassesFromBody();
        document.body.classList.add('active-' + what);
      }

      // Toggle same menu
      if (what === this.activeMenu && what !== '') {
        this.removeAllNavigationClassesFromBody();
        what = '';
      }

      this.activeMenu = what;
    },

    /**
     * Toggle main navigation status
     */
    toggleMainNavStatus (bool = undefined) {
      if (bool !== undefined) {
        this.mainNavStatus = bool;
      }
      else {
        this.mainNavStatus = !this.mainNavStatus;
      }
    },

    /**
     * Set start screen animation active state
     */
    setStartScreenAnimationActive (bool) {
      this.startScreenAnimationActive = bool;
    },

    // Track Characteristics
    setTrackStrokeColor (val) {
      this.trackStrokeColor = val;
    },

    setTrackStrokeWidth (val) {
      this.trackStrokeWidth = val;
    },

    setTrackColor (val) {
      this.trackColor = val;
    },

    setTrackWidth (int) {
      this.trackWidth = int;
    },

    setTrackOpacity (float) {
      this.trackOpacity = float;
    },

    /**
     * Show/hide demo import modal
     */
    setShowDemoImportModal (bool) {
      this.showDemoImportModal = bool;
    },

    /**
     * Show metadata info modal with calculation results
     * @param {{ filled: Array, warnings: Array }} info
     */
    showMetadataInfoModal (info) {
      this.metadataInfoModal = {
        visible: true,
        filled: info.filled || [],
        warnings: info.warnings || []
      };
    },

    /**
     * Hide metadata info modal
     */
    hideMetadataInfoModal () {
      this.metadataInfoModal = {
        visible: false,
        filled: [],
        warnings: []
      };
    },

    /**
     * Show tag selector modal
     * @param {{ selectedTagIds: Array, onSave: Function }} params
     */
    showTagSelectorModal (params) {
      this.tagSelectorModal = {
        visible: true,
        selectedTagIds: [...(params.selectedTagIds || [])],
        onSave: params.onSave || null
      };
    },

    /**
     * Hide tag selector modal
     */
    hideTagSelectorModal () {
      this.tagSelectorModal = {
        visible: false,
        selectedTagIds: [],
        onSave: null
      };
    },

    /**
     * Update selected tags in modal (called from modal itself)
     */
    updateTagSelectorModalSelection (tagIds) {
      this.tagSelectorModal.selectedTagIds = [...tagIds];
    }
  }
});
